import isNil from 'lodash/isNil';
// eslint-disable-next-line import/order
import {
	createHook,
	createSelector,
	type Action,
	type BoundActions,
	type HookFunction,
	type OutputSelector,
	type StoreActionApi,
} from 'react-sweet-state';

// eslint-disable-next-line import/order
import type {
	FieldId,
	FieldInternalValue,
	FieldMapFromIframe,
	FieldType,
} from '../../../../common/types/field';
// eslint-disable-next-line import/order
import { type Actions, store } from '../../main';
// eslint-disable-next-line import/order
import type {
	AppId,
	ChangeId,
	IssueAdjustmentsState,
	RegisteredFields,
	StoreContainerProps,
} from '../../types';
// eslint-disable-next-line import/order
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { ScreenTabsDataPublic } from 'packages/ui-modifications/core/src/common/types/screen-tabs';
import type {
	LookupData,
	TenantContext,
	ExecutionContext,
	ScreenTabsChangesPublic,
	FieldChangesMapPublic,
} from 'packages/ui-modifications/core/src/types';
import type { IntlShape } from 'react-intl';
// eslint-disable-next-line import/order
import type { IEnvironment } from 'relay-runtime';
import type { ErrorState } from '../../actions/errors/types';
import type { InitializeFieldPayload } from '../../actions/initialize-field';
import type { NotificationState } from '../../actions/notification/types';
import type { ProcessChangePayload } from '../../actions/process-change';
import type { AppError } from '../../utils/errors/types';

export type FieldIsProcessing = {
	isBusy: boolean;
	appsIds: string[];
};

/*
 * When there is an error in any part of UI modifications
 * we need to ensure that fields from the Jira view are not blocked by reacting on an error here.
 * e.g. GIC fields will not be blocked anymore when error occurs in one of the decorators.
 */
export const selectFieldIsProcessing: OutputSelector<
	IssueAdjustmentsState,
	string,
	{
		isBusy: boolean;
		appsIds: string[];
	}
> = createSelector(
	(state: IssueAdjustmentsState) => state.registeredFields,
	(state: IssueAdjustmentsState) => state.error,
	(_, fieldId: FieldId) => fieldId,
	(registeredChangesMap, error, fieldId) => {
		if (error) {
			return {
				isBusy: false,
				appsIds: [],
			};
		}

		const appsIds = Object.entries(registeredChangesMap)
			.filter(
				([_, registeredFields]) =>
					!isNil(registeredFields) && Object.values(registeredFields).flat().includes(fieldId),
			)
			.map(([appId]) => appId);

		return {
			isBusy: appsIds.length > 0,
			appsIds,
		};
	},
);

export const useFieldIsProcessing: HookFunction<
	FieldIsProcessing,
	BoundActions<
		IssueAdjustmentsState,
		{
			triggerFieldInitialization: () => ({
				getState,
				setState,
			}: StoreActionApi<IssueAdjustmentsState>) => void;
			processChange: ({
				fieldId,
				fieldValue,
				eventType,
			}: Omit<ProcessChangePayload, 'fieldType'>) => (
				{ setState, getState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			setFieldValue: ({
				fieldId,
				fieldType,
				fieldValue,
			}: {
				fieldId: FieldId;
				fieldType: FieldType;
				fieldValue: FieldInternalValue;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeField: ({
				field,
				metadata,
			}: InitializeFieldPayload) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			initializeFields: (
				fields: InitializeFieldPayload[],
			) => (
				{ setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			startFieldsInitialisationTimeout: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => ({ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			successFieldsInitalisation: ({
				createAnalyticsEvent,
			}: {
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => (
				{ getState, setState }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType }: StoreContainerProps,
			) => void;
			registerFields: ({
				changeId,
				fieldsToBeRegistered,
				appId,
			}: {
				changeId: ChangeId;
				fieldsToBeRegistered: RegisteredFields;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			unregisterFields: ({
				changeId,
				appId,
			}: {
				changeId: ChangeId;
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateFields: ({
				changeId,
				fieldsChanges,
				lookupDataFromApi,
				tenantContext,
				executionContext,
				environment,
				appId,
				intl,
				createAnalyticsEvent,
			}: {
				changeId: ChangeId;
				fieldsChanges: FieldMapFromIframe;
				lookupDataFromApi: Omit<LookupData, 'LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY'>;
				tenantContext: TenantContext;
				executionContext: ExecutionContext;
				environment: IEnvironment;
				appId: AppId;
				intl: IntlShape;
				createAnalyticsEvent: CreateUIAnalyticsEvent;
			}) => Action<IssueAdjustmentsState, StoreContainerProps, Promise<void>>;
			clearAppliedValue: (
				fieldId: FieldId,
			) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAllFields: () => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setError: (
				errorProperties: ErrorState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetError: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNumberOfSupportedFields: (
				numberOfAllSupportedFields: number,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setExperienceDataIsLoading: (
				experienceDataIsLoading: boolean,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setAppErrors: ({
				errors,
				appId,
			}: {
				errors: AppError[];
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetAppErrors: ({
				appId,
			}: {
				appId: AppId;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			addAppsErrors: ({
				errors,
			}: {
				errors: Record<AppId, AppError[]>;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeNoScreenTab: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			initializeScreenTabs: ({
				tabs,
				selected,
			}: {
				tabs: Readonly<
					Array<{
						id: string;
					}>
				>;
				selected?: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateScreenTabs: ({
				screenTabsChanges,
				changeId,
				appId,
			}: {
				screenTabsChanges: ScreenTabsChangesPublic;
				appId: AppId;
				changeId: ChangeId;
			}) => ({ getState, setState, dispatch }: StoreActionApi<IssueAdjustmentsState>) => void;
			updateActiveScreenTab: ({
				selected,
			}: {
				selected: number;
			}) => ({ getState, setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			detectAppliedChangesConflicts: ({
				appId,
				changeId,
			}: {
				appId: AppId;
				changeId: ChangeId;
			}) => (
				{ setState, getState, dispatch }: StoreActionApi<IssueAdjustmentsState>,
				{ viewType, createAnalyticsEvent }: StoreContainerProps,
			) => void;
			collectScreenTabsAppliedChangesHistory: ({
				appId,
				changeId,
				screenTabs,
			}: {
				appId: AppId;
				changeId: ChangeId;
				screenTabs: ScreenTabsDataPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			collectFieldsAppliedChangesHistory: ({
				appId,
				changeId,
				fieldsChanges,
			}: {
				appId: AppId;
				changeId: ChangeId;
				fieldsChanges: FieldChangesMapPublic;
			}) => ({ setState, getState }: StoreActionApi<IssueAdjustmentsState>) => void;
			setNotification: (
				notificationProperties: NotificationState,
			) => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
			resetNotification: () => ({ setState }: StoreActionApi<IssueAdjustmentsState>) => void;
		}
	>,
	string
> = createHook<IssueAdjustmentsState, Actions, FieldIsProcessing, FieldId>(store, {
	selector: selectFieldIsProcessing,
});
